import { splitEncoding } from '@contentful/live-preview';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { visuallyHidden } from '@mui/utils';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  Fragment,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { TextEffectCF } from '@/types';
import { textEffectColorMap } from '.';
import { renderAllTextAsSpans } from './utils';

export default function RainbowSlideVertical({
  entry,
}: {
  entry: TextEffectCF;
}) {
  const prefersReducedMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState(0);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const { colors, text } = entry.fields;

  const visuallyHiddenList = useRef<HTMLSpanElement>(null);

  const colorHexValues = (colors || []).map((color) => {
    const { cleaned } = splitEncoding(color);

    return textEffectColorMap[cleaned as typeof color];
  });

  const currentColor = colorHexValues[activeItem % colorHexValues.length];
  const textComponents = documentToReactComponents(text, {
    renderNode: renderAllTextAsSpans,
  }) as ReactNode[];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveItem((current) => (current + 1) % textComponents.length);
    }, 1300);

    return () => clearInterval(interval);
  }, [textComponents.length]);

  useLayoutEffect(() => {
    const calculateWidestElement = () => {
      requestAnimationFrame(() => {
        if (visuallyHiddenList.current) {
          let max = 0;

          Array.from(visuallyHiddenList.current.children).forEach((child) => {
            if (child instanceof HTMLElement) {
              max = Math.max(max, child.offsetWidth);
            }
          });

          setMaxWidth(max + 1);
        }
      });
    };

    calculateWidestElement();

    window.addEventListener('resize', calculateWidestElement);
    return () => window.removeEventListener('resize', calculateWidestElement);
  }, []);

  return (
    <Fragment>
      {/* This div is visually hidden. It's used for a11y and measuring the max width of longest element. */}
      <span style={visuallyHidden} ref={visuallyHiddenList}>
        {textComponents.map((component, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
          <span key={i}>{component}</span>
        ))}
      </span>

      <span
        aria-hidden='true'
        data-chromatic='ignore'
        style={{
          display: 'inline-block',
          overflow: 'hidden',
          minWidth: `${maxWidth}px`,
          verticalAlign: 'bottom',
        }}
      >
        <AnimatePresence mode='wait'>
          <motion.span
            key={activeItem}
            initial={{
              y: prefersReducedMotion ? '0%' : '100%',
              opacity: prefersReducedMotion ? 0 : 1,
            }}
            animate={{ y: '0%', opacity: 1 }}
            exit={{
              y: prefersReducedMotion ? '0%' : '-100%',
              opacity: prefersReducedMotion ? 0 : 1,
            }}
            transition={{
              duration: prefersReducedMotion ? 1.5 : 0.2,
              ease: 'easeInOut',
            }}
            style={{ display: 'inline-block', color: currentColor }}
          >
            {textComponents[activeItem]}
          </motion.span>
        </AnimatePresence>
      </span>
    </Fragment>
  );
}
